import { useCallback } from "react"
import { useMutation } from "urql"

import "../graphql/customerPurchaseOrderMutations"

import {
  type CustomerPurchaseOrderProduct,
  type CustomerPurchaseOrderProductUpsertInput,
  type CustomerPurchaseOrderUpsertProductsFailure,
  UpsertCustomerPurchaseOrderProductsDocument,
  type UpsertCustomerPurchaseOrderProductsMutation,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import { PERMISSION_DENIED_MUTATION_MESSAGE, isForbiddenError } from "@/utils/urql/permissionError"

interface BulkUpsertCustomerPurchaseOrderLineItemsParams {
  customerPurchaseOrderId: string
  products: CustomerPurchaseOrderProductUpsertInput[]
}

interface UseBulkUpsertCustomerPurchaseOrderLineItemsReturn {
  bulkUpsertCustomerPurchaseOrderLineItems: (
    params: BulkUpsertCustomerPurchaseOrderLineItemsParams
  ) => Promise<CustomerPurchaseOrderProduct[] | undefined>
  isSaving: boolean
}

export const useBulkUpsertCustomerPurchaseOrderLineItems = (): UseBulkUpsertCustomerPurchaseOrderLineItemsReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<UpsertCustomerPurchaseOrderProductsMutation>(
    UpsertCustomerPurchaseOrderProductsDocument
  )

  const bulkUpsertCustomerPurchaseOrderLineItems = useCallback(
    async (
      params: BulkUpsertCustomerPurchaseOrderLineItemsParams
    ): Promise<CustomerPurchaseOrderProduct[] | undefined> => {
      const { data, error } = await executeMutation({
        input: { customerPurchaseOrderId: params.customerPurchaseOrderId, products: params.products },
      })
      const output = data?.customerPurchaseOrder.upsertProducts
      const hasError = error || output?.__typename === "CustomerPurchaseOrderUpsertProductsFailure"

      if (hasError) {
        const message = isForbiddenError(error)
          ? PERMISSION_DENIED_MUTATION_MESSAGE
          : (error?.message ?? (output as CustomerPurchaseOrderUpsertProductsFailure).error.message)
        notify({ type: NotificationType.Error, message })
        return undefined
      }

      notify({ type: NotificationType.Success, message: "Changes saved" })
      return output?.__typename === "CustomerPurchaseOrderUpsertProductsSuccess"
        ? (output.customerPurchaseOrder.products as CustomerPurchaseOrderProduct[])
        : undefined
    },
    [executeMutation, notify]
  )

  return { bulkUpsertCustomerPurchaseOrderLineItems, isSaving: fetching }
}
